import { Loading01Icon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';
import {
  ColumnDef,
  RowSelectionState,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useCopy } from '@/components/CopyButton/useCopy';
import { DataGrid, DataGridPagination } from '@/components/DataGrid';
import {
  getTableSelectColumnDef,
  useDataGridSizingLocalStorage,
} from '@/components/DataGrid.utils';
import { DeleteConfirmationButton } from '@/components/Delete';
import { Switch } from '@/components/Switch/Switch';
import { Button, Tooltip } from '@/design-system';
import { useDataGridState } from '@/hooks/useDataGridState';
import { usePublicSharingDisabled } from '@/hooks/usePublicSharingDisabled';
import {
  useGenericDeleteMutation,
  useOrganizationId,
  useTenantBulkUnshareMutation,
  useTenantSharedEntities,
  useUpdateOrganization,
} from '@/hooks/useSwr';
import DatasetIcon from '@/icons/DatasetIcon';
import LinkIcon from '@/icons/LinkIcon.svg?react';
import ProjectIcon from '@/icons/ProjectIcon';
import { TenantSharedEntity } from '@/types/schema';
import {
  apiDatasetsPath,
  apiRunsPath,
  apiWorkspacesPath,
  appDatasetsPath,
  appOrganizationPath,
  appProjectsPath,
  appPublicDatasetsPath,
  appPublicPath,
  appRunPath,
  appSessionPath,
} from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { RunType } from '../Run/components/RunType';

const pluralRules = new Intl.PluralRules('en-US');

function SharedEntity(props: { entity: TenantSharedEntity }) {
  const organizationId = useOrganizationId();

  if (props.entity.type === 'dataset') {
    return (
      <Link
        to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${props.entity.dataset_id}`}
        className="flex items-center gap-1.5 hover:underline"
      >
        <DatasetIcon className="h-5 w-5 rounded border border-secondary bg-secondary text-tertiary" />
        <span>{props.entity.dataset_name ?? props.entity.dataset_id}</span>
      </Link>
    );
  }

  if (props.entity.type === 'run') {
    const runTag =
      props.entity.run_name &&
      props.entity.run_type &&
      props.entity.session_id ? (
        <Link
          to={`/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${props.entity.session_id}/${appRunPath}/${props.entity.run_id}`}
          className="flex items-center gap-1.5 hover:underline"
        >
          <RunType
            run={{
              name: props.entity.run_name,
              run_type: props.entity.run_type,
            }}
          />
          <span>{props.entity.run_name}</span>
        </Link>
      ) : null;

    const sessionTag =
      props.entity.session_id && props.entity.session_name ? (
        <Link
          to={`/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${props.entity.session_id}`}
          className="flex items-center gap-1.5 hover:underline"
        >
          <ProjectIcon className="h-5 w-5 rounded border border-secondary bg-secondary text-tertiary" />
          <span>{props.entity.session_name}</span>
        </Link>
      ) : null;

    if (!runTag || !sessionTag) {
      return 'Unknown Run';
    }

    return (
      <span className="flex items-center gap-2">
        {runTag}

        <span className="text-tertiary">in</span>
        {sessionTag}
      </span>
    );
  }

  return 'Unknown';
}

function SharedUrlCopy(props: { entity: TenantSharedEntity }) {
  let to = `/${appPublicPath}/${props.entity.share_token}`;
  if (props.entity.type === 'run') {
    to += `/${appRunPath}`;
  } else if (props.entity.type === 'dataset') {
    to += `/${appPublicDatasetsPath}`;
  }

  const { copied, onCopy } = useCopy({ copy: window.location.origin + to });
  return (
    <Tooltip title={copied ? 'Copied!' : 'Click to copy'} side="top">
      <Link
        to={to}
        onClick={(e) => {
          if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
            e.preventDefault();
            onCopy();
          }
        }}
        className="inline-flex cursor-pointer items-center gap-1.5 overflow-hidden text-sm"
      >
        <LinkIcon className="h-4 w-4 shrink-0" />
        <span>{props.entity.share_token}</span>
      </Link>
    </Tooltip>
  );
}

function SharedEntityUnshare(props: {
  entity: TenantSharedEntity;
  onSuccess?: () => void;
}) {
  const unshare = useGenericDeleteMutation(
    {
      endpoint:
        props.entity.type === 'run'
          ? `${apiRunsPath}/${props.entity.run_id}/share`
          : `${apiDatasetsPath}/${props.entity.dataset_id}/share`,
      invalidationPrefixes: [`${apiWorkspacesPath}/current/shared`],
    },
    { onSuccess: props.onSuccess }
  );

  return (
    <Button
      size="sm"
      variant="outlined"
      color="secondary"
      loading={unshare.isMutating}
      onClick={() => unshare.trigger()}
    >
      Unshare
    </Button>
  );
}

function SharedEntityBulkUnshare(props: {
  selected: TenantSharedEntity[];
  onSuccess?: () => void;
}) {
  const bulkUnshare = useTenantBulkUnshareMutation({
    onSuccess: props.onSuccess,
  });

  return (
    <Button
      size="sm"
      variant="outlined"
      color="error"
      loading={bulkUnshare.isMutating}
      onClick={() =>
        bulkUnshare.trigger({
          json: { share_tokens: props.selected.map((i) => i.share_token) },
        })
      }
    >
      Unshare
    </Button>
  );
}

const columnHelper = createColumnHelper<TenantSharedEntity>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const COLUMN_DEF: ColumnDef<TenantSharedEntity, any>[] = [
  getTableSelectColumnDef(),
  {
    id: 'entity',
    header: 'Entity',
    cell: ({ row }) => <SharedEntity entity={row.original} />,
    enableResizing: true,
  },
  {
    id: 'url',
    header: 'URL',
    cell: ({ row }) => <SharedUrlCopy entity={row.original} />,
    enableResizing: true,
  },
  columnHelper.accessor('created_at', {
    header: 'Created At',
    enableResizing: true,
    enableSorting: false,
  }),
  {
    id: 'action',
    header: 'Actions',
    cell: ({ row, table }) => {
      const { onUnshareSuccess } = (table.options.meta ?? {}) as {
        onUnshareSuccess?: (row: TenantSharedEntity) => void;
      };

      return (
        <SharedEntityUnshare
          entity={row.original}
          onSuccess={() => {
            onUnshareSuccess?.(row.original);
          }}
        />
      );
    },
    enableResizing: true,
  },
];

const EMPTY_TABLE = [];

export function OrganizationShared() {
  'use no memo';
  const { paginationModel, setPaginationModel } = useDataGridState({});
  const entities = useTenantSharedEntities({
    limit: paginationModel.pageSize,
    offset: paginationModel.pageIndex * paginationModel.pageSize,
  });

  const publicSharingDisabled = usePublicSharingDisabled();

  const updateOrganization = useUpdateOrganization();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [columnSizing, setColumnSizing] =
    useDataGridSizingLocalStorage('shared');

  const table = useReactTable({
    data: entities.data?.entities ?? EMPTY_TABLE,
    meta: {
      onUnshareSuccess: (row: TenantSharedEntity) => {
        setRowSelection((prev) => {
          const next = { ...prev };
          delete next[row.share_token];
          return next;
        });
      },
    },
    columns: COLUMN_DEF,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.share_token,
    state: { rowSelection, columnSizing, pagination: paginationModel },
    onPaginationChange: setPaginationModel,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    manualPagination: true,
    columnResizeMode: 'onChange',
    pageCount: Math.ceil(
      +(entities.data?.headers?.['x-pagination-total'] || 0) /
        paginationModel.pageSize
    ),
  });

  const nSelected = Object.keys(rowSelection).length;

  return (
    <div className="flex flex-col gap-4 overflow-auto">
      <div className="flex items-start gap-1">
        <div className="flex flex-grow flex-col gap-1">
          <h2 className={cn('font-semibold', 'text-lg')}>Shared URLs</h2>
          <p className="text-sm text-tertiary">
            View and manage all public and privately shared links within this
            organization
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          label="Disable public sharing"
          checked={publicSharingDisabled}
          onChange={async (checked) => {
            await updateOrganization.trigger({
              json: { public_sharing_disabled: checked },
            });
          }}
        />
        <Loading01Icon
          className={cn(
            'size-4 animate-spin',
            updateOrganization.isMutating ? 'opacity-100' : 'opacity-0'
          )}
        />
        <DeleteConfirmationButton
          title="Unshare all"
          description="This will unshare all shared URLs for this organization."
          deleteButtonText="Unshare all"
          isDeleting={updateOrganization.isMutating}
          onDelete={async () => {
            await updateOrganization.trigger({
              json: { unshare_all: true },
            });
            entities.mutate();
          }}
          trigger={
            <Button size="sm" variant="outlined" color="error">
              Unshare all
            </Button>
          }
        ></DeleteConfirmationButton>
      </div>
      {entities.isLoading ? (
        <div>
          <LinearProgress />
        </div>
      ) : (
        <>
          <div className="relative rounded-lg border border-secondary">
            <DataGrid
              cellHeight={56}
              cellSpacingVariant="wide"
              table={table}
              isLoading={entities.isLoading}
            />
          </div>
          <DataGridPagination table={table} />
        </>
      )}

      {Object.keys(rowSelection).length > 0 && (
        <div className="fixed bottom-3 left-1/2 -translate-x-1/2">
          <div className="flex items-center gap-4 rounded-md border border-secondary bg-popover p-2 pl-4 pr-2 shadow-md">
            <span className="text-md">
              {
                {
                  one: `1 URL selected`,
                  two: `${nSelected} URLs selected`,
                  few: `${nSelected} URLs selected`,
                  other: `${nSelected} URLs selected`,
                }[pluralRules.select(nSelected)]
              }
            </span>

            <div className="flex items-center gap-2">
              <SharedEntityBulkUnshare
                selected={table
                  .getSelectedRowModel()
                  .rows.map((i) => i.original)}
                onSuccess={() => setRowSelection({})}
              />
              <Button
                size="sm"
                variant="plain"
                color="primary"
                onClick={() => setRowSelection({})}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
